import { useQuery } from '@tanstack/react-query'
import {
  getIotDeviceNotificationSettingKey,
  getNotificationsKey,
  getNotificationsSettingsByModuleKey,
  getNotificationsSettingsKey,
  getNotificationUnreadCountKey,
} from 'common/constants/queryKeys'

import {
  getIotDeviceNotificationSetting,
  getNotificationUnreadCount,
  getNotifications,
  getNotificationsSettingAll,
  getNotificationsSettingByModule,
} from '.'
import {
  IotDeviceNotificationSetting,
  NotificationCountResponse,
  NotificationResponse,
  NotificationSettingResponse,
} from './types'

export const useGetNotificationUnreadCount = () => {
  return useQuery<NotificationCountResponse>({
    queryKey: getNotificationUnreadCountKey(),
    queryFn: async () => {
      const { data } = await getNotificationUnreadCount()
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetNotifications = (id?: string, page?: number, pageSize?: number) => {
  return useQuery<NotificationResponse>({
    queryKey: getNotificationsKey({ id, page, pageSize }),
    queryFn: async () => {
      const { data } = await getNotifications({ id, page, pageSize })
      return data
    },
    staleTime: 0,
    // ปิด refetch ตอน window focus กลับมา — ไม่งั้น react-query จะ refetch ทุกหน้า
    // ที่เคยโหลดไปแล้วรัวๆ (หน้า 1..N) ทุกครั้งที่คลิกจอ (blur→focus) ทำให้ backend
    // ตีกลับ 429 Too Many Requests เมื่อเลื่อนไปหลายหน้าแล้ว
    refetchOnWindowFocus: false,
  })
}

export const useGetNotificationsSettings = (page?: number, pageSize?: number) => {
  return useQuery<NotificationSettingResponse>({
    queryKey: getNotificationsSettingsKey({ page, pageSize }),
    queryFn: async () => {
      const { data } = await getNotificationsSettingAll({ page, pageSize })
      return data
    },
    staleTime: Infinity,
  })
}

// DOF-1309: ค่าสวิตช์ของอุปกรณ์นี้ "ของ user คนที่เรียกเท่านั้น" สองคนเรียก
// อุปกรณ์เดียวกันได้ค่าไม่เหมือนกันได้ ไม่ใช่ bug
export const useIotDeviceNotificationSetting = (deviceId: string) => {
  return useQuery<IotDeviceNotificationSetting | null>({
    queryKey: getIotDeviceNotificationSettingKey(deviceId),
    queryFn: async () => {
      const { data } = await getIotDeviceNotificationSetting(deviceId)
      return data.data
    },
    enabled: !!deviceId,
    staleTime: 0,
  })
}

export const useGetNotificationsSettingsByModule = (
  moduleCode?: string,
  page?: number,
  pageSize?: number
) => {
  return useQuery<NotificationSettingResponse>({
    queryKey: getNotificationsSettingsByModuleKey({ moduleCode, page, pageSize }),
    queryFn: async () => {
      const { data } = await getNotificationsSettingByModule(moduleCode, { page, pageSize })
      return data
    },
    staleTime: Infinity,
  })
}
